import { getMyUser } from '@/store'
import { updateClientMeta } from '@/api/user'
import { MAX_RECENT_SEARCHES } from '@/helpers/constants'
import isEqual from 'lodash/isEqual'
import router from '@/router'
import { createLogger } from '@/helpers/log'
import { getChat, isChatInPinned, isChatInRecents } from '@/store/chats'

export const log = createLogger('Search')

export const escapeHTML = (unsafeText) => {
	let div = document.createElement('div')
	div.innerText = unsafeText
	return div.innerHTML
}

export const HIGHLIGHT_START = '<pronto_hl>'
export const HIGHLIGHT_END = '</pronto_hl>'
const TEMP_HIGHLIGHT_START = '[[highlight]]'
const TEMP_HIGHLIGHT_END = '[[/highlight]]'
const HIGHLIGHT_FINAL_START = '<span class="search-highlight">'
const HIGHLIGHT_FINAL_END = '</span>'

export const scrubHighlight = (text) => {
	if (!text) return text
	const replaced = text.replace(/^"|"$/g, '')
	// escape any highlighted portions
	const escaped = replaced
		.replaceAll(HIGHLIGHT_START, TEMP_HIGHLIGHT_START)
		.replaceAll(HIGHLIGHT_END, TEMP_HIGHLIGHT_END)
	// escape any (potentially) unsafe html
	const encoded = escapeHTML(escaped)
	// properly render any unicode characters (and emojis)
	// See https://stackoverflow.com/a/9932955/1313904

	// Try to JSON.parse()
	// If we get a failure (because it contains a regex
	// value or something) then just do the highlight
	//
	try {
		var { key: normalized } = JSON.parse(
			`{"key":"${encoded.replaceAll('"', '\\"')}"}`
		)
	} catch (e) {
		const unescaped = encoded
			.replaceAll(TEMP_HIGHLIGHT_START, HIGHLIGHT_FINAL_START)
			.replaceAll(TEMP_HIGHLIGHT_END, HIGHLIGHT_FINAL_END)
		return unescaped.replace(/\\n/g, '<br>')
	}

	// unescape the highlighted portions
	const unescaped = normalized
		.replaceAll(TEMP_HIGHLIGHT_START, HIGHLIGHT_FINAL_START)
		.replaceAll(TEMP_HIGHLIGHT_END, HIGHLIGHT_FINAL_END)
	// replace string newlines with html line breaks
	return unescaped.replace(/\\n/g, '<br>')
}

export const setSearchQueryParams = (params = {}, replace = false) => {
	const prevParams = router.currentRoute.value.query
	// loop through all params and see if the values have changed
	const query = Object.entries(params).reduce(
		(acc, [key, value]) => {
			if (Array.isArray(value) && value.length > 0)
				acc[key] = value.sort().join(',')
			else if (value && !Array.isArray(value)) acc[key] = value
			else delete acc[key]
			return acc
		},
		{ ...prevParams }
	)
	// if no params have changed, we can bail out
	if (isEqual(query, prevParams)) {
		return log('no params have changed', query, params, prevParams)
	}
	// and now we can update the router
	log('updating search params', query, params, prevParams)
	if (replace) {
		router.replace({ name: 'search', query })
	} else {
		router.push({ name: 'search', query })
	}
}

export const getRecentSearches = () => {
	const user = getMyUser()
	const recents = user.client_meta?.recent_searches || []
	return recents.filter((r) => r.query)
}

export const saveRecentSearch = async (queryData) => {
	if (!queryData?.query) return
	// delete some page-specific params
	delete queryData.search_type
	delete queryData.size
	delete queryData.from
	const prevRecents = getRecentSearches()
	const prevIndex = prevRecents.findIndex((s) => s.query === queryData.query)
	if (prevIndex >= 0) {
		const prevSearch = prevRecents[prevIndex]
		const isSame = isEqual(queryData, prevSearch)
		// if this is the same as the most-recent query, just ignore it and bail
		if (prevSearch === 0 && isSame) return prevRecents
		// this is the same text as previous query, so just move it to the top
		prevRecents[prevIndex] = null
	}
	// put this query at the top of the list
	const recents = [queryData]
		.concat(prevRecents)
		.filter((x) => x)
		.slice(0, MAX_RECENT_SEARCHES)
	// persist the full list in client_meta
	log('saving recent search', queryData)
	await updateClientMeta({ recent_searches: recents })
	return recents
}

export const removeRecentSearch = async (queryStr) => {
	// remove any previous searches for this query
	const recents = getRecentSearches().filter((r) => r.query !== queryStr)
	// persist the new list in client_meta
	log('removing recent search', queryStr)
	await updateClientMeta({ recent_searches: recents })
	return recents
}

export const getLinkToChatForMessage = (message) => {
	const { id, bubble_id } = message
	const param = `?centered=${id}`
	const chat = getChat(bubble_id)
	if (isChatInPinned(chat)) return `/pinned/${bubble_id}${param}`
	if (isChatInRecents(chat)) return `/recents/${bubble_id}${param}`
	return `/chat/${bubble_id}${param}`
}
